import { useFxWorkspaceContext } from '@mobile-mapping/providers';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import {
  Component,
  JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
} from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';

import type { FxWorkspaceFolderDescendantRefTrnsGetter } from './fx-workspace-draft-checkbox-sources';
import {
  MissionTreeNodeData,
  MissionTreeNodeType,
} from './project-data-tree-nodes';
import { WorkspaceTreeItem } from './workspace-tree-nodes';

export type FxWorkspaceDraftCheckboxCategory =
  | 'missions'
  | 'models'
  | 'pointClouds'
  | 'trajectories'
  | 'mapWorkspaces';

function isMissionTreeNodeData(data: unknown): data is MissionTreeNodeData {
  if (typeof data !== 'object' || data === null || !('type' in data)) {
    return false;
  }

  const { type } = data;
  return (
    type === MissionTreeNodeType.Mission ||
    type === MissionTreeNodeType.Path ||
    type === MissionTreeNodeType.Runs ||
    type === MissionTreeNodeType.Run
  );
}

export function getProjectDataReferenceRefTrn<T>(
  category: FxWorkspaceDraftCheckboxCategory,
  node: TreeNodeDefinition<T>
): string | undefined {
  switch (category) {
    case 'missions': {
      // Only the mission node is selectable. The path and the runs below it
      // never get a checkbox. A mission reference is its mission TRN.
      if (!isMissionTreeNodeData(node.data)) {
        return undefined;
      }

      return node.data.type === MissionTreeNodeType.Mission
        ? node.data.mission?.missionTrn
        : undefined;
    }
    case 'models': {
      const item = node.data as { type?: string } | undefined;
      if (node.isLeaf === false || item?.type === 'FOLDER') {
        return undefined;
      }

      return node.id;
    }
    case 'pointClouds':
    case 'trajectories':
      if (node.isLeaf === false || node.data === undefined) {
        return undefined;
      }

      return node.id;
    case 'mapWorkspaces': {
      const data = node.data as WorkspaceTreeItem | undefined;
      return data?.kind === 'layer' ? node.id : undefined;
    }
    default:
      throw new UnreachableCaseError(category);
  }
}

export function fxWorkspaceDraftTrailingContent<T>(
  category: FxWorkspaceDraftCheckboxCategory,
  node: TreeNodeDefinition<T>,
  isEditing: boolean,
  getFolderDescendantRefTrns: FxWorkspaceFolderDescendantRefTrnsGetter
): JSX.Element | undefined {
  if (!isEditing) {
    return undefined;
  }

  const leafRefTrn = getProjectDataReferenceRefTrn(category, node);
  if (leafRefTrn !== undefined) {
    return <FxWorkspaceDraftLeafCheckbox refTrn={leafRefTrn} />;
  }

  const descendantRefTrns = getFolderDescendantRefTrns(node.id);
  if (descendantRefTrns.length === 0) {
    return undefined;
  }

  return (
    <FxWorkspaceDraftFolderCheckbox
      nodeId={node.id}
      leafRefTrns={descendantRefTrns}
    />
  );
}

const stopTreeRowInteraction = (event: Event): void => {
  event.stopPropagation();
};

type ModusWcCheckboxHost = HTMLElement & {
  checked: boolean;
  value: boolean;
  indeterminate: boolean;
};

const syncModusWcCheckbox = (
  el: HTMLElement | undefined,
  checked: boolean,
  indeterminate = false
): void => {
  if (el === undefined) {
    return;
  }

  const host = el as ModusWcCheckboxHost;
  host.checked = checked;
  host.value = checked;
  host.indeterminate = indeterminate;

  // modus-wc-checkbox paints checked and indeterminate from the inner
  // input in shadowRoot. Light-DOM querySelector cannot reach that control.
  const input =
    el.shadowRoot?.querySelector('input[type="checkbox"]') ??
    el.querySelector('input[type="checkbox"]');
  if (input instanceof HTMLInputElement) {
    input.checked = checked;
    input.indeterminate = indeterminate;
  }
};

const FxWorkspaceDraftCheckboxRow: Component<{
  readonly children: JSX.Element;
}> = (props): JSX.Element => (
  <span
    class="inline-flex h-10 items-center pr-2"
    onClick={stopTreeRowInteraction}
    onPointerDown={stopTreeRowInteraction}
  >
    {props.children}
  </span>
);

const FxWorkspaceDraftLeafCheckbox: Component<{
  readonly refTrn: string;
}> = (props): JSX.Element => {
  const fxWorkspace = useFxWorkspaceContext();
  const isChecked = createMemo(() =>
    fxWorkspace.isDraftReference(props.refTrn)
  );
  const [checkboxEl, setCheckboxEl] = createSignal<HTMLElement>();

  createEffect(() => {
    syncModusWcCheckbox(checkboxEl(), isChecked());
  });

  return (
    <FxWorkspaceDraftCheckboxRow>
      <modus-wc-checkbox
        // @ts-expect-error Solid ref is valid but absent from modus-wc-checkbox JSX types
        ref={setCheckboxEl}
        size="lg"
        class="shrink-0"
        attr:data-testid={`workspace-draft-leaf-checkbox-${props.refTrn}`}
        value={isChecked()}
        on:inputChange={(event: Event) => {
          stopTreeRowInteraction(event);
          fxWorkspace.toggleDraftReference(props.refTrn);
        }}
      />
    </FxWorkspaceDraftCheckboxRow>
  );
};

const FxWorkspaceDraftFolderCheckbox: Component<{
  readonly nodeId: string;
  readonly leafRefTrns: readonly string[];
}> = (props): JSX.Element => {
  const fxWorkspace = useFxWorkspaceContext();
  const selectedCount = createMemo(
    () =>
      props.leafRefTrns.filter((refTrn) => fxWorkspace.isDraftReference(refTrn))
        .length
  );
  const isChecked = createMemo(
    () =>
      props.leafRefTrns.length > 0 &&
      selectedCount() === props.leafRefTrns.length
  );
  const isIndeterminate = createMemo(
    () => selectedCount() > 0 && selectedCount() < props.leafRefTrns.length
  );
  const showCheckbox = createMemo(() => props.leafRefTrns.length > 0);
  const [checkboxEl, setCheckboxEl] = createSignal<HTMLElement>();

  createEffect(() => {
    syncModusWcCheckbox(checkboxEl(), isChecked(), isIndeterminate());
  });

  const handleToggle = (event: Event): void => {
    stopTreeRowInteraction(event);
    fxWorkspace.setDraftReferenceSelection(props.leafRefTrns, !isChecked());
  };

  return (
    <Show when={showCheckbox()}>
      <FxWorkspaceDraftCheckboxRow>
        <modus-wc-checkbox
          // @ts-expect-error Solid ref is valid but absent from modus-wc-checkbox JSX types
          ref={setCheckboxEl}
          size="lg"
          class="shrink-0"
          attr:data-testid={`workspace-draft-folder-checkbox-${props.nodeId}`}
          value={isChecked()}
          indeterminate={isIndeterminate()}
          on:inputChange={handleToggle}
        />
      </FxWorkspaceDraftCheckboxRow>
    </Show>
  );
};
